import {
  GalleryItem,
  GalleryItemType,
  ShopCategory,
  AdNode,
} from '@/api/types/shop.types';
import { ROOT_CATEGORY_ID } from '@/components/Shop/Shop.constants';

export function buildGalleryItems(
  photos: { url: string }[],
  videos: { url: string }[],
  t: (key: string) => string
): GalleryItem[] {
  const ytItems: GalleryItem[] = videos
    .filter(v => v.url.includes('youtube.com/watch?v='))
    .map(v => ({
      type: GalleryItemType.YouTube,
      videoId: new URL(v.url).searchParams.get('v') || '',
      alt: t('shop.youtube_alt'),
    }));

  const photoItems: GalleryItem[] = photos.map(p => ({
    type: GalleryItemType.Image,
    src: p.url,
    alt: t('shop.img_alt'),
  }));

  return [...ytItems, ...photoItems];
}

export function isTextOverflowing(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight;
}

export const youtubeReady: Promise<void> = new Promise(resolve => {
  if (typeof window !== 'undefined') {
    /* eslint-disable */
    if (window.YT && typeof window.YT.Player === 'function') {
      resolve();
    } else {
      const w = window as typeof window & {
        onYouTubeIframeAPIReady: () => void;
      };
      w.onYouTubeIframeAPIReady = () => resolve();
    }
    /* eslint-enable */
  }
});

export const handleMobileVideoStart = async (
  idx: number,
  videoId: string,
  setActiveVideoIndices: React.Dispatch<React.SetStateAction<Set<number>>>
) => {
  setActiveVideoIndices(prev => new Set(prev).add(idx));
  await youtubeReady;
  /* eslint-disable */
  const el = document.getElementById(`yt-player-${idx}`);
  if (!el || typeof window.YT?.Player !== 'function') return;

  if (
    typeof window.YT !== 'undefined' &&
    typeof window.YT.Player === 'function'
  ) {
    new window.YT.Player(el, {
      videoId,
      playerVars: { rel: 0, modestbranding: 1, controls: 1, autoplay: 1 },
      events: {
        onReady: (event: YT.PlayerEvent) => {
          event.target.playVideo();
        },
      },
    });
  }
  /* eslint-enable */
};

export function normalizeCategoryPath(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

export function buildCategoryAdsCountMap(
  adsList: AdNode[],
  availableCategories: ShopCategory[] = []
): Map<string, number> {
  const categoryAdsCount = new Map<string, number>();

  adsList.forEach(ad => {
    const pathParts = ad.path.split('/').filter(Boolean);
    if (pathParts.length >= 2) {
      const fullCategoryPath = pathParts.slice(0, -1).join('/');

      if (availableCategories.length > 0) {
        const sortedCategories = [...availableCategories]
          .filter(cat => cat.id !== ROOT_CATEGORY_ID)
          .sort((a, b) => {
            const aPath = normalizeCategoryPath(a.path);
            const bPath = normalizeCategoryPath(b.path);
            return bPath.split('/').length - aPath.split('/').length;
          });

        const matchedCategory = sortedCategories.find(cat => {
          const catPath = normalizeCategoryPath(cat.path);
          return (
            fullCategoryPath === catPath ||
            fullCategoryPath.startsWith(`${catPath}/`)
          );
        });

        if (matchedCategory) {
          const matchedPath = normalizeCategoryPath(matchedCategory.path);
          categoryAdsCount.set(
            matchedPath,
            (categoryAdsCount.get(matchedPath) || 0) + 1
          );
        } else {
          categoryAdsCount.set(
            fullCategoryPath,
            (categoryAdsCount.get(fullCategoryPath) || 0) + 1
          );
        }
      } else {
        categoryAdsCount.set(
          fullCategoryPath,
          (categoryAdsCount.get(fullCategoryPath) || 0) + 1
        );
      }
    }
  });

  return categoryAdsCount;
}

export function hasMoreSpecificCategory(
  catPath: string,
  currentCatId: string,
  allCategories: ShopCategory[],
  categoryAdsCount: Map<string, number>
): boolean {
  return allCategories.some(otherCat => {
    if (otherCat.id === ROOT_CATEGORY_ID || otherCat.id === currentCatId)
      return false;
    const otherCatPath = normalizeCategoryPath(otherCat.path);
    return (
      otherCatPath.startsWith(`${catPath}/`) &&
      categoryAdsCount.has(otherCatPath)
    );
  });
}

export function shouldShowCategory(
  cat: ShopCategory,
  categoryAdsCount: Map<string, number>,
  allCategories: ShopCategory[]
): boolean {
  if (cat.id === ROOT_CATEGORY_ID) return true;

  const catPath = normalizeCategoryPath(cat.path);
  if (categoryAdsCount.has(catPath)) return true;

  const hasSubcategories = Array.from(categoryAdsCount.keys()).some(adPath =>
    adPath.startsWith(`${catPath}/`)
  );

  if (!hasSubcategories) return false;

  return !hasMoreSpecificCategory(
    catPath,
    cat.id,
    allCategories,
    categoryAdsCount
  );
}

export function calculateCategoryCount(
  cat: ShopCategory,
  categoryAdsCount: Map<string, number>,
  allCategories: ShopCategory[]
): number {
  if (cat.id === ROOT_CATEGORY_ID) return cat.count;

  const catPath = normalizeCategoryPath(cat.path);
  let count = categoryAdsCount.get(catPath) || 0;

  if (count === 0) {
    const catPathParts = catPath.split('/');
    if (catPathParts.length === 2) {
      if (
        !hasMoreSpecificCategory(
          catPath,
          cat.id,
          allCategories,
          categoryAdsCount
        )
      ) {
        for (const [adPath, adCount] of categoryAdsCount.entries()) {
          if (adPath.startsWith(`${catPath}/`)) {
            count += adCount;
          }
        }
      }
    }
  }

  return count;
}
